/**
 * THE breadcrumb trail (wave W2, Mahmoud's UAT of 2026-10-10).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * His finding: breadcrumbs existed on some screens and not others, and they did not take
 * him back where he CAME from. Now there is one trail, drawn by the shell under its bar on
 * every screen, fed per route, always the same depth:
 *
 *   Home                        on Home
 *   Home / register             on a register (Tasks and inbox, Findings, the calendar)
 *   Home / register / record    on a record (CTR-18, FND-9, an inspection)
 *
 * The REGISTER crumb of a record is the register you came from, exactly as you left it:
 * its filters and its queue ride in its search (`/itps?queue=review`), so going back by
 * the crumb lands on the same list. Opened from a record (a project, then its contract)
 * the trail keeps the register the first record was opened from. Opened from Home or by
 * a link, it is the record's own register. The origin of each history entry is kept in
 * the session, so Back and a reload keep the trail.
 *
 * ⭐ WAVE EXPERIENCE A: THE TRAIL IS THE JOURNEY, NOT THE DATA HIERARCHY. A record opened
 * from another record keeps the whole walk: Home / PRJ-3 / ITP-0004 / the item, each crumb
 * the exact view it was (its search, so its filters, ride along). Opened from Home it is
 * Home / the record; from a register, Home / the register as left / the record; by a link
 * or a reload with nothing remembered, Home / the record's own register / the record.
 * Walking back to a record already on the trail cuts the trail there (no loops), and a
 * very long walk keeps Home and its last steps. Each history entry keeps its journey in
 * the session, so Back, Forward and a reload draw the same trail.
 *
 * The RECORD crumb names the record (CTR-18) once the page has read it; a page publishes
 * that name through `RecordCrumb` (or the older per-page `Breadcrumbs`, which now feeds
 * this trail instead of drawing its own). Until then the record kind stands in.
 */

import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavigationType, useLocation, useNavigationType } from "react-router";

import { strings } from "../brand/strings";

/** One crumb: a link, except the current page. */
export interface TrailCrumb {
  readonly label: string;
  readonly to?: string;
}

/** The registers: every top-level screen, by its path. */
export const REGISTERS: Readonly<Record<string, string>> = {
  "/": strings.nav.home,
  "/dashboard": strings.nav.dashboard,
  "/tasks": strings.nav.tasks,
  "/itps": strings.nav.itps,
  "/schedules": strings.nav.schedules,
  "/rfis": strings.nav.rfis,
  "/inspections": strings.nav.inspections,
  "/findings": strings.nav.findings,
  "/actions": strings.nav.actions,
  "/guide": strings.nav.guide,
  "/configuration": strings.nav.configuration,
  "/calendar": strings.nav.calendar,
  "/lessons": strings.nav.lessonsLearned,
  "/evaluations": strings.evaluations.heading,
  "/docket": strings.nav.witnessDocket,
  "/portfolio/projects": strings.nav.projectsContracts,
  "/portfolio/parties": strings.nav.partiesContractors,
  "/programs/engineering": strings.nav.programEngineering,
  "/programs/procurement": strings.nav.programProcurement,
  "/programs/construction": strings.nav.programConstruction,
  "/programs/external-audits": strings.nav.programExternalAudits,
  "/account": strings.nav.account,
  "/outbox": strings.nav.outbox,
  "/standards": strings.nav.standards,
  "/credentials": strings.nav.credentials,
  "/audit": strings.nav.audit,
};

/** The record routes: their own register, and the word that stands in for the name. */
const RECORDS: readonly { pattern: RegExp; register: string; kind: string }[] = [
  { pattern: /^\/itps\/[^/]+$/, register: "/itps", kind: strings.crumbs.plan },
  { pattern: /^\/schedules\/[^/]+$/, register: "/schedules", kind: strings.crumbs.schedule },
  { pattern: /^\/activities\/[^/]+$/, register: "/schedules", kind: strings.crumbs.activity },
  { pattern: /^\/rfis\/[^/]+$/, register: "/rfis", kind: strings.crumbs.rfi },
  {
    pattern: /^\/inspections\/[^/]+$/,
    register: "/inspections",
    kind: strings.crumbs.inspection,
  },
  { pattern: /^\/findings\/[^/]+$/, register: "/findings", kind: strings.crumbs.finding },
  { pattern: /^\/actions\/[^/]+$/, register: "/actions", kind: strings.crumbs.action },
  {
    pattern: /^\/portfolio\/projects\/[^/]+$/,
    register: "/portfolio/projects",
    kind: strings.crumbs.project,
  },
  {
    pattern: /^\/portfolio\/contracts\/[^/]+$/,
    register: "/portfolio/projects",
    kind: strings.crumbs.contract,
  },
];

/** Trailing slashes off, so "/itps/" and "/itps" are one register. */
function normal(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

export function isRegister(pathname: string): boolean {
  return REGISTERS[normal(pathname)] !== undefined;
}

export function recordRoute(
  pathname: string,
): { readonly register: string; readonly kind: string } | null {
  const path = normal(pathname);
  return RECORDS.find((one) => one.pattern.test(path)) ?? null;
}

/** Where a step of the journey was: its path and the search it carried. */
export interface CrumbOrigin {
  readonly path: string;
  readonly search: string;
}

/** The most steps a journey keeps after Home: a long walk keeps its last steps. */
export const JOURNEY_STEPS = 5;

const HOME_CRUMB: TrailCrumb = { label: strings.nav.home, to: "/" };

/**
 * The trail for one location. Pure: the shell supplies the journey that led here (the
 * crumbs before this page, Home first) and the record's name if the page published it.
 * With no journey, a record falls back to its own register as last left.
 */
export function crumbTrail(
  pathname: string,
  journey: readonly TrailCrumb[] | null,
  recordLabel: string | null,
  remembered: (register: string) => string = () => "",
): TrailCrumb[] {
  const path = normal(pathname);
  if (path === "/") {
    return [{ label: strings.nav.home }];
  }
  const register = REGISTERS[path];
  if (register !== undefined) {
    return [HOME_CRUMB, { label: register }];
  }
  const record = recordRoute(path);
  if (record === null) {
    return [HOME_CRUMB, { label: strings.crumbs.notFound }];
  }
  const before =
    journey !== null && journey.length > 0
      ? journey
      : [
          HOME_CRUMB,
          {
            label: REGISTERS[record.register] ?? record.register,
            to: `${record.register}${remembered(record.register)}`,
          },
        ];
  return [...before, { label: recordLabel ?? record.kind }];
}

function pathOf(to: string | undefined): string {
  return normal((to ?? "").split("?")[0] ?? "");
}

/**
 * The journey that leads to `target` when it is reached from `before`. Pure.
 *
 * `beforeJourney` is the journey that led to `before` (null when none was kept);
 * `beforeLabel` the name `before` published. Only a record has a journey; null means
 * the trail falls back to the record's own register.
 */
export function nextJourney(
  target: string,
  before: CrumbOrigin | null,
  beforeJourney: readonly TrailCrumb[] | null,
  beforeLabel: string | null,
): TrailCrumb[] | null {
  const path = normal(target);
  if (recordRoute(path) === null || before === null) {
    return null;
  }
  const from = normal(before.path);
  let journey: TrailCrumb[];
  if (from === "/") {
    journey = [HOME_CRUMB];
  } else if (REGISTERS[from] !== undefined) {
    journey = [HOME_CRUMB, { label: REGISTERS[from], to: `${from}${before.search}` }];
  } else {
    const record = recordRoute(from);
    if (record === null) {
      return null;
    }
    const base =
      beforeJourney !== null && beforeJourney.length > 0
        ? [...beforeJourney]
        : [
            HOME_CRUMB,
            { label: REGISTERS[record.register] ?? record.register, to: record.register },
          ];
    journey = [...base, { label: beforeLabel ?? record.kind, to: `${from}${before.search}` }];
  }
  // Back to a record already on the trail: the trail is cut there, never looped.
  const seen = journey.findIndex((crumb) => crumb !== HOME_CRUMB && pathOf(crumb.to) === path);
  if (seen > 0) {
    journey = journey.slice(0, seen);
  }
  if (journey.length > JOURNEY_STEPS + 1) {
    journey = [HOME_CRUMB, ...journey.slice(journey.length - JOURNEY_STEPS)];
  }
  return journey;
}

/* ---------------------------------------------------------------------- */
/* The record's name: published by the page, read by the shell's trail.     */
/* ---------------------------------------------------------------------- */

export interface CrumbFeed {
  readonly publish: (pathname: string, label: string | null) => void;
}

export const CrumbContext = createContext<CrumbFeed | null>(null);

/** Whether the shell's trail is present (a page drawn outside it draws its own). */
export function useCrumbFeed(): CrumbFeed | null {
  return useContext(CrumbContext);
}

/** Name the record this page shows on the shell's trail. Renders nothing. */
export function RecordCrumb({ label }: { readonly label: string }): null {
  const feed = useCrumbFeed();
  const { pathname } = useLocation();
  useEffect(() => {
    feed?.publish(pathname, label);
    return () => {
      feed?.publish(pathname, null);
    };
  }, [feed, pathname, label]);
  return null;
}

/* ---------------------------------------------------------------------- */
/* The shell's trail and the origins it remembers.                          */
/* ---------------------------------------------------------------------- */

const JOURNEYS_KEY = "qualify.crumbs.journeys";
const SEARCHES_KEY = "qualify.crumbs.searches";
const KEEP = 200;

function readMap(key: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function writeMap(key: string, value: Record<string, unknown>): void {
  try {
    const entries = Object.entries(value);
    sessionStorage.setItem(key, JSON.stringify(Object.fromEntries(entries.slice(-KEEP))));
  } catch {
    // Storage full or off: the trail falls back to the record's own register.
  }
}

function asJourney(value: unknown): TrailCrumb[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const crumbs: TrailCrumb[] = [];
  for (const one of value) {
    if (typeof one !== "object" || one === null) {
      return null;
    }
    const record = one as Record<string, unknown>;
    if (typeof record.label !== "string") {
      return null;
    }
    crumbs.push(
      typeof record.to === "string"
        ? { label: record.label, to: record.to }
        : { label: record.label },
    );
  }
  return crumbs;
}

/**
 * Work out, once per history entry, the journey that led here, and remember it under the
 * entry's key (so Back, Forward and a reload find the same trail). `labels` holds the
 * name each record page published, by pathname, so the step just left is named.
 */
function useJourney(labels: { readonly current: Record<string, string> }): TrailCrumb[] | null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef<{ key: string; pathname: string; search: string } | null>(null);
  const [journey, setJourney] = useState<TrailCrumb[] | null>(
    () => asJourney(readMap(JOURNEYS_KEY)[location.key]) ?? null,
  );

  useLayoutEffect(() => {
    const journeys = readMap(JOURNEYS_KEY);
    const path = normal(location.pathname);
    // A register remembers the search it was last seen with (its filters, its queue),
    // so a record opened by a link still leads back to the list as it was left.
    if (isRegister(path) && path !== "/") {
      const searches = readMap(SEARCHES_KEY);
      searches[path] = location.search;
      writeMap(SEARCHES_KEY, searches);
    }
    let next: TrailCrumb[] | null = asJourney(journeys[location.key]);
    if (recordRoute(path) !== null && (next === null || navigationType !== NavigationType.Pop)) {
      const before = previous.current;
      next = nextJourney(
        path,
        before === null ? null : { path: before.pathname, search: before.search },
        before === null ? null : asJourney(journeys[before.key]),
        before === null ? null : (labels.current[normal(before.pathname)] ?? null),
      );
      if (next !== null) {
        journeys[location.key] = next;
        writeMap(JOURNEYS_KEY, journeys);
      }
    }
    previous.current = {
      key: location.key,
      pathname: location.pathname,
      search: location.search,
    };
    setJourney(next);
  }, [location.key, location.pathname, location.search, navigationType, labels]);

  return journey;
}

function rememberedSearch(register: string): string {
  const value = readMap(SEARCHES_KEY)[register];
  return typeof value === "string" ? value : "";
}

/** The shell's one trail, under its bar, on every screen. */
export function ShellBreadcrumbs({
  recordLabel,
}: {
  /** The name the current page published, for its own pathname only. */
  readonly recordLabel: { readonly pathname: string; readonly label: string } | null;
}): React.JSX.Element {
  const location = useLocation();
  // Every name a record page has published, by pathname: the journey names the step left.
  const labels = useRef<Record<string, string>>({});
  useEffect(() => {
    if (recordLabel !== null) {
      labels.current[normal(recordLabel.pathname)] = recordLabel.label;
    }
  }, [recordLabel]);
  const journey = useJourney(labels);
  const label =
    recordLabel !== null && recordLabel.pathname === location.pathname ? recordLabel.label : null;
  const trail = crumbTrail(location.pathname, journey, label, rememberedSearch);
  return (
    <nav
      className="q-crumbs q-crumbs--shell"
      data-testid="breadcrumbs"
      aria-label={strings.crumbs.label}
    >
      {trail.map((crumb, index) =>
        crumb.to === undefined ? (
          <span className="q-crumbs__here" key={index} aria-current="page">
            {crumb.label}
          </span>
        ) : (
          <Link
            className="q-crumbs__link"
            key={index}
            to={crumb.to}
            data-testid={`crumb-${String(index)}`}
          >
            {crumb.label}
          </Link>
        ),
      )}
    </nav>
  );
}

/** The feed the shell provides: the current page's record name, by pathname. */
export function useCrumbState(): {
  readonly feed: CrumbFeed;
  readonly recordLabel: { readonly pathname: string; readonly label: string } | null;
} {
  const [recordLabel, setRecordLabel] = useState<{
    readonly pathname: string;
    readonly label: string;
  } | null>(null);
  const [feed] = useState<CrumbFeed>(() => ({
    publish: (pathname, label) => {
      setRecordLabel((current) => {
        if (label === null) {
          return current !== null && current.pathname === pathname ? null : current;
        }
        return current !== null && current.pathname === pathname && current.label === label
          ? current
          : { pathname, label };
      });
    },
  }));
  return { feed, recordLabel };
}
